import { useInfiniteQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'
import { AGENT_URL } from '../config/env'
import { ApiError } from '../lib/api'
import type { CallRecord } from '../features/calls/types'
import type { FilterCondition } from '../utils/filterUtils'
import {
  CALL_PROCESSING_MAX_AGE_MS,
  CALL_PROCESSING_POLL_INTERVAL_MS,
  QUERY_GC_TIME_MS,
  QUERY_STALE_TIME_MS,
} from '../config/constants'
import { callKeys } from '../lib/queryKeys'

export interface CallsQueryParams {
  // Structured filters (field/operator/value) from DataTable
  filters?: FilterCondition[]

  // Filtering
  type?: string
  status?: string
  ended_reason?: string
  patient_id?: string
  duty_id?: string
  duty_name?: string
  category?: string
  label?: string
  patient_first_name?: string
  patient_last_name?: string
  created_after?: string
  created_before?: string

  // Pagination
  limit?: number

  // Sorting
  sort_by?: string
  sort_order?: 'asc' | 'desc'
}

export function computeIsActivelyProcessing(call: {
  status?: string | null
  ended_reason?: unknown
  label?: unknown
}): boolean {
  const activeStatuses = new Set(['initiated', 'processing', 'ended'])
  return (
    activeStatuses.has((call.status as string | null) ?? '') &&
    (!call.ended_reason || !call.label)
  )
}

export function shouldRefetch(
  pages:
    | Array<{
        data: Array<{ isActivelyProcessing?: boolean; created_at: string }>
      }>
    | undefined,
  now?: number
): number | false {
  const oneHourAgo = (now ?? Date.now()) - CALL_PROCESSING_MAX_AGE_MS
  const hasProcessing = pages?.some((page) =>
    page.data.some(
      (call) =>
        call.isActivelyProcessing &&
        new Date(call.created_at).getTime() > oneHourAgo
    )
  )
  return hasProcessing ? CALL_PROCESSING_POLL_INTERVAL_MS : false
}

export interface CallsResponse {
  data: CallRecord[]
  pagination: {
    limit: number
    has_more: boolean
    next_cursor: string | null
    total: number | null
  }
}

export function getNextCallsPageParam(
  lastPage: CallsResponse
): string | undefined {
  return lastPage.pagination.next_cursor ?? undefined
}

async function fetchCalls(
  token: string,
  params: CallsQueryParams,
  cursor?: string
): Promise<CallsResponse> {
  const queryParams = new URLSearchParams()

  // Add all params to query string
  if (params.limit) queryParams.append('limit', params.limit.toString())
  if (cursor) queryParams.append('cursor', cursor)
  if (params.sort_by) queryParams.append('sort_by', params.sort_by)
  if (params.sort_order) queryParams.append('sort_order', params.sort_order)

  // Structured filters: convert to query string format filter[field][operator]=value
  if (params.filters && params.filters.length > 0) {
    for (const filter of params.filters) {
      const value = filter.value.trim()
      if (!value) continue

      queryParams.append(`filter[${filter.field}][${filter.operator}]`, value)
    }
  }

  const response = await fetch(
    `${AGENT_URL}/data/calls?${queryParams.toString()}`,
    {
      headers: {
        Authorization: token,
      },
    }
  )

  if (!response.ok) {
    const errorText = await response.text()
    let errorMessage = `Failed to fetch calls: ${response.status}`
    try {
      const errorData = JSON.parse(errorText)
      errorMessage = errorData.error || errorMessage
    } catch {
      // If error response isn't JSON, use the text or default message
      if (errorText) errorMessage = errorText
    }
    throw new ApiError(errorMessage, response.status)
  }

  const data = await response.json()

  // Transform the response to match CallRecord type
  // Backend returns call_id as UUID, phone as string, created_at as datetime string
  return {
    ...data,
    data: data.data.map((call: Record<string, unknown>) => {
      // Convert call_id (UUID) to string
      const callId = call.call_id || call.id

      // Convert phone string to number if present
      let phone: number | null = null
      if (call.phone) {
        if (typeof call.phone === 'string') {
          // Remove any non-digit characters and parse
          const phoneStr = call.phone.replace(/\D/g, '')
          phone = phoneStr ? parseInt(phoneStr, 10) : null
        } else if (typeof call.phone === 'number') {
          phone = call.phone
        }
      }

      // Ensure created_at is a string (ISO format)
      const createdAt = call.created_at || call.date

      let createdAtStr: string
      if (typeof createdAt === 'string') {
        createdAtStr = createdAt
      } else if (createdAt instanceof Date) {
        createdAtStr = createdAt.toISOString()
      } else {
        createdAtStr = new Date().toISOString()
      }

      const isActivelyProcessing = computeIsActivelyProcessing(call)

      return {
        ...call,
        call_id: typeof callId === 'string' ? callId : String(callId),
        phone: phone,
        created_at: createdAtStr,
        duration: call.duration || call.call_duration || null,
        isActivelyProcessing,
      }
    }),
  }
}

/**
 * Hook to fetch and paginate call records with server-side sorting and filtering.
 *
 * Uses infinite query with cursor-based pagination for efficient data loading.
 *
 * @param params - Query parameters including filters, sorting, and limit
 * @returns Infinite query result with pages of call data
 */
export function useCallsQuery(
  params: CallsQueryParams = {},
  options: { enabled?: boolean } = {}
) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useInfiniteQuery({
    queryKey: callKeys.list(organization?.id as string, params),
    enabled: !!organization?.id && (options.enabled ?? true),
    queryFn: async ({ pageParam }) => {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }
      return fetchCalls(token, params, pageParam as string | undefined)
    },
    getNextPageParam: getNextCallsPageParam,
    initialPageParam: undefined as string | undefined,
    staleTime: QUERY_STALE_TIME_MS,
    gcTime: QUERY_GC_TIME_MS,
    // Disable automatic refetching on window focus to reduce unnecessary requests
    refetchOnWindowFocus: false,
    // Poll every 10s while active-status rows have incomplete data.
    // ACTIVE_STATUSES: states where processing is still happening.
    // The 1-hour guard stops polling for `ended` calls that got stuck (analysis never completed).
    refetchInterval: (query) =>
      shouldRefetch(query.state.data?.pages, Date.now()),
  })
}
